// ─────────────────────────────────────────────────────────────────────────────
// Pure helpers for the file-intelligence pipeline (WS3).
//
// Two side-effect-free functions shared across realms so both call sites emit
// the SAME turn-input string the backend harness protocol expects, and so the
// Identify button's result rendering can be unit-tested without the DOM:
//
//   * buildFileReceivedTurnText — the `[file received] …` turn body. Used by the
//     extension's linked-AI-group file dispatch (message_key variant) and by the
//     btgpt web upload composer (url variant). The format is a backend contract
//     — keep it verbatim.
//   * formatFileIntelResult — turns a FileIntelResult into display lines + a meta
//     line for the inline "Identify" render (mirrors how transcribe-inline
//     renders text + a dot-separated metadata line).
//
// No store, no fetch, no DOM — importable from extension content scripts, the
// website SPA, and jest alike.
// ─────────────────────────────────────────────────────────────────────────────

/** Invoice fields the file-intel backend extracts (all optional). */
export interface FileIntelInvoice {
  vendor?: string;
  doc_number?: string;
  date?: string;
  total?: number | string;
  vat?: number | string;
  currency?: string;
}

/** Result shape returned by the `file-intel-identify` action (backend contract). */
export interface FileIntelResult {
  doc_kind?: string;
  summary?: string;
  confidence?: number;
  language?: string;
  invoice?: FileIntelInvoice;
}

export interface FileReceivedTurnInput {
  /** Original file name; defaults to `file` when missing/blank. */
  filename?: string;
  /** MIME type as reported by WA / the upload; passed through verbatim. */
  mimetype?: string;
  /** WA message key — used for linked-AI-group messages (`message_key=…`). */
  messageKey?: string;
  /** Durable public URL — used for btgpt-web uploads (`url=…`). */
  url?: string;
  /** Anything the user typed alongside the file (btgpt web); appended below. */
  userText?: string;
  /** Browser-local coarse classification (the on-device tier). When present
   *  the agent routes on it: invoice-like kinds go to the heavy server
   *  `identify`; everything else is acknowledged + queried WITHOUT a server
   *  round-trip. Omit for `unknown`/no-guess so the agent identifies as before. */
  clientKind?: FileKind | string;
  /** 0–1 confidence of `clientKind`. */
  clientConfidence?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Browser-local coarse file classification (the "first basic recognition" tier).
//
// Runs entirely client-side (CLIP zero-shot on images, pdf.js text + keyword
// scan on documents) so the agent can decide, WITHOUT a server call, whether a
// file is worth the heavy server OCR (`identify`/`ingest_invoice`) or should
// just be acknowledged and queried ("looks like a dog 🐕 — what should I do?").
// Deliberately coarse: it only needs to route, not to read the document.
// ─────────────────────────────────────────────────────────────────────────────

/** Coarse client-side classification of a received file. */
export type FileKind =
  | 'invoice'
  | 'receipt'
  | 'agreement'
  | 'offer'
  | 'id_document'
  | 'document'
  | 'photo'
  | 'product'
  | 'screenshot'
  | 'other'
  | 'unknown';

/** Kinds that warrant transferring the file to the server OCR/ingest path. */
export const INVOICE_LIKE_KINDS: ReadonlySet<FileKind> = new Set<FileKind>(['invoice', 'receipt']);

export function isInvoiceLikeKind(kind?: string | null): boolean {
  return !!kind && (INVOICE_LIKE_KINDS as ReadonlySet<string>).has(kind);
}

/** Bilingual (he/en) keyword table for document text. First match with the
 *  most hits wins; a keyword miss returns `unknown` so the caller lets the
 *  server identify (a text PDF is cheap server-side and safer than a wrong
 *  skip). Order is only a tie-break — invoice/receipt before agreement/offer. */
const DOC_KEYWORD_TABLE: ReadonlyArray<{ kind: FileKind; terms: RegExp[] }> = [
  {
    kind: 'invoice',
    terms: [/\binvoice\b/i, /\btax invoice\b/i, /\bvat\b/i, /חשבונית/, /מע["״'׳]?מ/, /לתשלום/, /סה["״'׳]?כ/],
  },
  { kind: 'receipt', terms: [/\breceipt\b/i, /קבלה/, /אישור תשלום/, /שולם/] },
  {
    kind: 'agreement',
    terms: [/\bagreement\b/i, /\bcontract\b/i, /terms\s+(and|&)\s+conditions/i, /הסכם/, /חוזה/, /בין הצדדים/, /תנאי שימוש/],
  },
  { kind: 'offer', terms: [/\bquote\b/i, /\bquotation\b/i, /\bproposal\b/i, /price\s+offer/i, /הצעת מחיר/, /הצעה\b/] },
];

/**
 * PDF-generator metadata masquerading as a text layer (ported from backend
 * file-intel/identify.ts). Measured live: MoneHash electricity bills (real,
 * image-only PDFs) carry a `;`-delimited CSV record of generator fields —
 * `…;Original;MoneHash;<reversed-hebrew>;…; CreatePdf<n>END;` — repeated 2-3×,
 * with NO invoice content. It passes length checks (plain ASCII/Hebrew), so a
 * dedicated signature is needed, else the keyword scan sees only junk and
 * returns `unknown` for a real invoice. Callers route these to the image path.
 */
export function looksLikeGeneratorMetadata(text: string | null | undefined): boolean {
  const t = (text ?? '').trim();
  if (!t) return false;
  // Unambiguous generator markers — no real invoice text contains these.
  if (/\b(Create|Append)Pdf\w*END\b/i.test(t)) return true;
  // Semicolon-CSV dominated AND highly repetitive: split on the record marker
  // and check if the same record repeats (MoneHash emits the identical row 3×).
  const semis = (t.match(/;/g) ?? []).length;
  if (semis >= 8 && semis / t.length > 0.02) {
    const records = t.split(/END;?/i).map((r) => r.trim()).filter((r) => r.length > 20);
    if (records.length >= 2 && new Set(records).size < records.length) return true;
  }
  return false;
}

/** Classify a document's extracted text into a coarse kind. Pure + bilingual;
 *  returns `unknown` (confidence 0) when no keyword fires so the caller falls
 *  back to the server identify. */
export function classifyDocTextToKind(text: string | null | undefined): { kind: FileKind; confidence: number } {
  const t = (text || '').trim();
  if (t.length < 8) return { kind: 'unknown', confidence: 0 };
  let best: { kind: FileKind; hits: number } = { kind: 'unknown', hits: 0 };
  for (const row of DOC_KEYWORD_TABLE) {
    let hits = 0;
    for (const re of row.terms) if (re.test(t)) hits++;
    if (hits > best.hits) best = { kind: row.kind, hits };
  }
  if (best.hits === 0) return { kind: 'unknown', confidence: 0 };
  // 1 hit → 0.60, 2 → 0.75, 3+ → capped 0.90 (coarse, monotonic).
  const confidence = Math.min(0.9, 0.45 + best.hits * 0.15);
  return { kind: best.kind, confidence };
}

/** Map the server identify's `doc_kind` (FileIntelResult, Claude extraction)
 *  onto the coarse client FileKind used for the in-chat reply + agent hint.
 *  The browser-local CLIP model was removed (measured live: on a real scanned
 *  invoice it answered "a screenshot" at 0.40) — images and scans are now
 *  identified by the SERVER pipeline (Claude vision), and this is the bridge
 *  between its vocabulary and the display/routing one. */
export function docKindToFileKind(docKind: string | null | undefined): FileKind {
  switch (docKind) {
    case 'invoice':
    case 'tax_invoice_receipt':
    case 'credit_note':
      return 'invoice';
    case 'receipt':
      return 'receipt';
    case 'contract':
      return 'agreement';
    case 'statement':
      return 'document';
    case 'other':
      return 'other';
    default:
      return 'unknown';
  }
}

/** Kinds that are clearly NOT a document — a manual Identify on these is fully
 *  answered on-device (no server OCR to run). */
export const PHOTO_KINDS: ReadonlySet<FileKind> = new Set<FileKind>(['photo', 'product', 'screenshot']);

export function isPhotoKind(kind?: string | null): boolean {
  return !!kind && (PHOTO_KINDS as ReadonlySet<string>).has(kind);
}

/** Emoji + human label for a coarse on-device FileKind (he+en-neutral). */
export function prettyFileKind(kind?: string | null): string {
  const map: Record<string, string> = {
    invoice: '🧾 Invoice',
    receipt: '🧾 Receipt',
    agreement: '📄 Agreement / contract',
    offer: '💰 Price offer',
    id_document: '🪪 ID document',
    document: '📄 Document',
    photo: '🖼️ Photo',
    product: '🛍️ Product photo',
    screenshot: '📱 Screenshot',
    other: '📎 File',
    unknown: '📎 File',
  };
  return (kind && map[kind]) || map.unknown;
}

/**
 * Build the turn-input text for a received file. The backend harness protocol
 * keys on this exact prefix:
 *
 *   linked-AI-group file → `[file received] name="<filename>" mimetype=<mimetype> message_key=<id._serialized>`
 *   btgpt-web upload      → `[file received] name="<filename>" mimetype=<mimetype> url=<gcs public url>`
 *
 * When `userText` is present it is appended on a new line so a caption like
 * "this is a receipt, file it" rides along with the file reference.
 */
export function buildFileReceivedTurnText(input: FileReceivedTurnInput): string {
  const name = (input.filename && input.filename.trim()) || 'file';
  const mimetype = input.mimetype ?? '';
  const ref = input.messageKey
    ? `message_key=${input.messageKey}`
    : `url=${input.url ?? ''}`;
  // On-device classification hint — appended only when the client actually
  // produced a guess (never for 'unknown'/no-guess, so the agent identifies
  // as before). The agent reads this to route without a server round-trip.
  let kindPart = '';
  if (input.clientKind && input.clientKind !== 'unknown') {
    const conf =
      typeof input.clientConfidence === 'number' && !Number.isNaN(input.clientConfidence)
        ? ` confidence=${input.clientConfidence.toFixed(2)}`
        : '';
    kindPart = ` kind=${input.clientKind}${conf}`;
  }
  const line = `[file received] name="${name}" mimetype=${mimetype} ${ref}${kindPart}`;
  const extra = input.userText?.trim();
  return extra ? `${line}\n${extra}` : line;
}

/** Human-friendly label for a doc_kind enum value. */
export function prettyDocKind(kind?: string): string {
  if (!kind) return 'Document';
  const map: Record<string, string> = {
    invoice: 'Invoice',
    tax_invoice_receipt: 'Tax Invoice / Receipt',
    receipt: 'Receipt',
    credit_note: 'Credit note',
    statement: 'Statement',
    contract: 'Contract',
    other: 'Document',
  };
  if (map[kind]) return map[kind];
  // Fallback: snake_case → Title Case ("purchase_order" → "Purchase Order").
  return kind
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export interface FormattedFileIntel {
  /** Primary lines: doc kind header, summary, and invoice fields when present. */
  lines: string[];
  /** Trailing metadata (confidence, language) — rendered small/dimmed. */
  meta: string[];
}

/**
 * Format a FileIntelResult for inline display. Returns primary `lines` and a
 * dot-joinable `meta` list — the caller escapes + renders (RTL-safe via
 * `dir="auto"` on the host element).
 */
export function formatFileIntelResult(result: FileIntelResult | null | undefined): FormattedFileIntel {
  const lines: string[] = [];
  const meta: string[] = [];
  if (!result) {
    return { lines: ['No result'], meta };
  }
  lines.push(prettyDocKind(result.doc_kind));
  if (result.summary) lines.push(result.summary);

  const inv = result.invoice;
  if (inv) {
    if (inv.vendor) lines.push(`Vendor: ${inv.vendor}`);
    if (inv.doc_number) lines.push(`Invoice #: ${inv.doc_number}`);
    if (inv.date) lines.push(`Date: ${inv.date}`);
    if (inv.total !== undefined && inv.total !== null && inv.total !== '') {
      lines.push(`Total: ${inv.total}${inv.currency ? ` ${inv.currency}` : ''}`);
    }
    if (inv.vat !== undefined && inv.vat !== null && inv.vat !== '') {
      lines.push(`VAT: ${inv.vat}${inv.currency ? ` ${inv.currency}` : ''}`);
    }
  }

  if (typeof result.confidence === 'number' && !Number.isNaN(result.confidence)) {
    // Accept either a 0–1 probability or an already-scaled percentage.
    const pct = result.confidence <= 1 ? Math.round(result.confidence * 100) : Math.round(result.confidence);
    meta.push(`confidence ${pct}%`);
  }
  if (result.language) meta.push(result.language);

  return { lines, meta };
}
